import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Loader2, TrendingUp, ArrowUp, ArrowDown, Clock, DollarSign, RefreshCw, Calendar, History } from 'lucide-react';
import { configApi, ExchangeRate as ExchangeRateType } from '../../api/config.api';
import { useToast } from '../../hooks/use-toast';

// Mock data (not used anymore, type reference kept for safety)
// const mockCurrentRate ... 
// const mockHistory ...

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-HT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export function ExchangeRate() {
    const { toast } = useToast();
    const [currentRate, setCurrentRate] = useState<ExchangeRateType | null>(null);
    const [history, setHistory] = useState<ExchangeRateType[]>([]);
    const [loading, setLoading] = useState(true);
    const [newRate, setNewRate] = useState('');
    const [saving, setSaving] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [rate, hist] = await Promise.all([
                configApi.getCurrentRate(),
                configApi.getRateHistory(10)
            ]);
            setCurrentRate(rate);
            setHistory(hist);
        } catch (err) {
            console.error('Error loading exchange rate:', err);
            toast({ title: 'Erè', description: 'Pa kapab chaje to echanj', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSaveRate = async () => {
        const rate = parseFloat(newRate);
        if (isNaN(rate) || rate <= 0) {
            toast({ title: 'Erè', description: 'Tanpri antre yon to valid', type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const newRateData = await configApi.setRate(rate);

            setCurrentRate(newRateData);
            setHistory(prev => [newRateData, ...prev.slice(0, 9)]);
            setNewRate('');
            toast({ title: 'Siksè', description: 'To echanj aktyalize', type: 'success' });
        } catch (err) {
            console.error('Error saving rate:', err);
            toast({ title: 'Erè', description: 'Pa kapab sovgade to echanj', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const getRateChange = (index: number) => {
        if (index >= history.length - 1) return null;
        const current = history[index].taux;
        const previous = history[index + 1].taux;
        return current - previous;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                <span className="ml-3 text-slate-600">Chajman to echanj...</span>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6">
            {/* Header info */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary-100 rounded-lg shrink-0">
                        <TrendingUp className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900">To Echanj USD → HTG</h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Defini to echanj aktyèl pou konvèsyon USD nan HTG.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Current Rate Card */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-6 text-white shadow-lg relative overflow-hidden">
                        {/* Background Decoration */}
                        <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                        <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-white/10 rounded-full blur-xl" />

                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-4">
                                <DollarSign className="h-5 w-5 text-green-100" />
                                <span className="text-sm font-medium text-green-50">To Aktyèl</span>
                            </div>
                            <div className="text-4xl sm:text-5xl font-bold mb-2 tracking-tight">
                                {currentRate?.taux?.toFixed(2)} <span className="text-xl sm:text-2xl font-normal text-green-100">HTG</span>
                            </div>
                            <p className="text-sm text-green-100/90 font-medium">pou 1.00 USD</p>
                            <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 text-xs sm:text-sm text-green-50">
                                <Clock className="h-3.5 w-3.5" />
                                <span>Mizajou: {currentRate ? formatDate(currentRate.created_at) : '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Update Rate Form */}
                    <div className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                            <RefreshCw className="h-4 w-4 text-primary-500" />
                            Mete To Ajou
                        </h4>
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-slate-500 uppercase">Nouvo To (HTG)</label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={newRate}
                                        onChange={(e) => setNewRate(e.target.value)}
                                        className="pr-12 h-10 text-lg font-mono"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">
                                        HTG
                                    </span>
                                </div>
                            </div>
                            <Button
                                onClick={handleSaveRate}
                                disabled={saving || !newRate}
                                className="w-full h-10 text-base font-medium"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Ap sovgade...
                                    </>
                                ) : (
                                    'Konfime Nouvo To'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Rate History */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
                        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                            <h4 className="font-medium text-slate-900 flex items-center gap-2">
                                <History className="h-4 w-4 text-slate-500" />
                                Istorik To Echanj
                            </h4>
                        </div>

                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50/30">
                                        <TableHead className="w-[180px]">Dat</TableHead>
                                        <TableHead className="text-right">To (HTG)</TableHead>
                                        <TableHead className="text-right">Chanjman</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {history.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center py-12 text-slate-500">
                                                Pa gen istorik disponib
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        history.map((rate, index) => {
                                            const change = getRateChange(index);
                                            return (
                                                <TableRow key={rate.id} className="hover:bg-slate-50/50">
                                                    <TableCell className="text-slate-600 font-medium whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                                            {formatDate(rate.created_at)}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right font-mono font-medium text-slate-900">
                                                        {rate.taux.toFixed(2)}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {change !== null && (
                                                            <div className="flex justify-end">
                                                                <Badge
                                                                    variant="outline"
                                                                    className={`gap-1 font-mono ${change > 0
                                                                        ? 'text-green-700 border-green-200 bg-green-50'
                                                                        : change < 0
                                                                            ? 'text-red-700 border-red-200 bg-red-50'
                                                                            : 'text-slate-500 bg-slate-50'
                                                                        }`}
                                                                >
                                                                    {change > 0 ? (
                                                                        <ArrowUp className="h-3 w-3" />
                                                                    ) : change < 0 ? (
                                                                        <ArrowDown className="h-3 w-3" />
                                                                    ) : (
                                                                        <span className="w-3 h-3 block border-2 border-current rounded-full opacity-20" />
                                                                    )}
                                                                    {change > 0 ? '+' : ''}{change.toFixed(2)}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
